/**
 * @fileoverview A monthly line chart card for the Sales Performance screen.
 * @description A titled card holding one month-by-month line chart, or an empty state when the
 * period has nothing to chart. The screen shows two: targets achieved and banks onboarded.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import PerformanceCard from '@/features/sales/components/PerformanceCard'
import PerformanceMonthChart from '@/features/sales/components/PerformanceMonthChart'
import TableEmptyState from '@/features/sales/components/TableEmptyState'

import type { MonthPoint } from '@/features/sales/components/PerformanceMonthChart'

interface PerformanceTrendCardProps {
  title: string
  subtitle: string
  points: MonthPoint[]
  colorClass: string
  testId: string
}

/**
 * @component
 * @param {PerformanceTrendCardProps} props - The chart's heading and data.
 * @param {string} props.title - The card's heading.
 * @param {string} props.subtitle - A line saying what is counted.
 * @param {MonthPoint[]} props.points - One point per month of the period.
 * @param {string} props.colorClass - The Tailwind text colour of the line.
 * @param {string} props.testId - `data-testid` of the card; the chart gets `-chart` appended.
 */
function PerformanceTrendCard({
  title,
  subtitle,
  points,
  colorClass,
  testId,
}: PerformanceTrendCardProps) {
  const { t } = useTranslation()
  const hasData = points.some((point) => point.value > 0)

  return (
    <PerformanceCard title={title} subtitle={subtitle} testId={testId}>
      {hasData ? (
        <PerformanceMonthChart
          points={points}
          colorClass={colorClass}
          ariaLabel={title}
          testId={`${testId}-chart`}
        />
      ) : (
        <TableEmptyState
          message={t('sales.salesPerformance.charts.emptyTitle')}
          description={t('sales.salesPerformance.charts.emptyDescription')}
          icon="trend"
        />
      )}
    </PerformanceCard>
  )
}

export default PerformanceTrendCard
